import { motion } from "framer-motion";
import { Mail, Phone, MapPin, MessageCircle, Send, Loader2 } from "lucide-react";
import { useLocalizedContent } from "@/hooks/use-localized-content";
import { useTranslation } from "react-i18next";
import { useWhatsApp } from "@/hooks/use-whatsapp";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PhoneInput } from "@/components/ui/phone-input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const Contact = () => {
  const { c, content } = useLocalizedContent();
  const { t } = useTranslation();

  const badge = c("contact_badge", "contact.badge");
  const title = c("contact_title", "contact.title");
  const subtitle = c("contact_subtitle", "contact.subtitle");
  const phone = content.contact_phone || "+351 915 883 513";
  const email = content.contact_email || "contacto@mentesa.pt";
  const location = content.contact_location || "Paço de Arcos, Oeiras, Portugal";
  const { url: whatsappUrl, enabled: whatsappEnabled, open: openWhatsApp } = useWhatsApp();
  const whatsappText = c("contact_whatsapp_text", "contact.whatsappCta");

  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const titleWords = title.split(" ");
  const highlight = titleWords.slice(-4).join(" ");
  const before = titleWords.slice(0, -4).join(" ");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.subject.trim() || !form.message.trim()) {
      toast.error(t("contact.formFillAll"));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error(t("contact.formInvalidEmail"));
      return;
    }
    setSending(true);
    try {
      const { error } = await supabase.functions.invoke("send-contact-message", { body: form });
      if (error) throw error;
      toast.success(t("contact.formSent"));
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err: any) {
      console.error(err);
      toast.error(t("contact.formError"));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contacto" className="py-24 bg-secondary/30">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto text-center"
        >
          <span className="inline-block rounded-full bg-accent px-4 py-1.5 text-xs font-semibold text-accent-foreground mb-4">
            {badge}
          </span>
          <h2 className="font-display text-4xl md:text-5xl font-bold mb-4">
            {before}{" "}
            <span className="text-gradient-wine">{highlight}</span>
          </h2>
          <p className="text-muted-foreground text-lg mb-12 leading-relaxed">{subtitle}</p>

          <div className="grid sm:grid-cols-3 gap-6 mb-12">
            <div className="rounded-2xl bg-card border border-border p-6 flex flex-col items-center gap-3 hover:shadow-md hover:border-primary/20 transition-all">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Phone size={22} className="text-primary" />
              </div>
              <span className="text-sm text-muted-foreground font-medium">{phone}</span>
            </div>
            <div className="rounded-2xl bg-card border border-border p-6 flex flex-col items-center gap-3 hover:shadow-md hover:border-primary/20 transition-all">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Mail size={22} className="text-primary" />
              </div>
              <span className="text-sm text-muted-foreground font-medium">{email}</span>
            </div>
            <div className="rounded-2xl bg-card border border-border p-6 flex flex-col items-center gap-3 hover:shadow-md hover:border-primary/20 transition-all">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <MapPin size={22} className="text-primary" />
              </div>
              <span className="text-sm text-muted-foreground font-medium">{location}</span>
            </div>
          </div>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-2xl mx-auto bg-card border border-border rounded-3xl p-8 md:p-10 shadow-sm space-y-5 text-left"
        >
          <div className="text-center mb-2">
            <h3 className="font-display text-2xl font-bold">{c("contact_form_title", "contact.formTitle")}</h3>
            <p className="text-sm text-muted-foreground mt-1">{c("contact_form_subtitle", "contact.formSubtitle")}</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contact-name">{c("contact_form_name_label", "contact.formNameLabel")} *</Label>
              <Input
                id="contact-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={c("contact_form_name_placeholder", "contact.formNamePlaceholder")}
                maxLength={120}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-email">{c("contact_form_email_label", "contact.formEmailLabel")} *</Label>
              <Input
                id="contact-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder={c("contact_form_email_placeholder", "contact.formEmailPlaceholder")}
                maxLength={200}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>{c("contact_form_phone_label", "contact.formPhoneLabel")} *</Label>
            <PhoneInput
              value={form.phone}
              onChange={(v) => setForm({ ...form, phone: v })}
              placeholder={c("contact_form_phone_placeholder", "contact.formPhonePlaceholder")}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact-subject">{c("contact_form_subject_label", "contact.formSubjectLabel")} *</Label>
            <Input
              id="contact-subject"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder={c("contact_form_subject_placeholder", "contact.formSubjectPlaceholder")}
              maxLength={200}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact-message">{c("contact_form_message_label", "contact.formMessageLabel")} *</Label>
            <Textarea
              id="contact-message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder={c("contact_form_message_placeholder", "contact.formMessagePlaceholder")}
              rows={6}
              maxLength={5000}
              required
            />
            <div className="text-xs text-muted-foreground text-right">{form.message.length}/5000</div>
          </div>

          <Button type="submit" disabled={sending} className="w-full rounded-full py-6 text-base font-semibold">
            {sending ? (
              <><Loader2 className="mr-2 animate-spin" size={18} /> {c("contact_form_sending", "contact.formSending")}</>
            ) : (
              <><Send className="mr-2" size={18} /> {c("contact_form_send", "contact.formSend")}</>
            )}
          </Button>
        </motion.form>

        <div className="text-center mt-10">
          {whatsappEnabled && (
            <motion.a
              href={whatsappUrl}
              onClick={openWhatsApp}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center gap-3 rounded-full bg-primary px-10 py-4 text-lg font-semibold text-primary-foreground shadow-lg shadow-primary/25 hover:shadow-xl transition-all"
            >
              <MessageCircle size={22} />
              {whatsappText}
            </motion.a>
          )}
          <p className="text-xs text-muted-foreground mt-4">{c("contact_response_guarantee", "contact.responseGuarantee")}</p>
        </div>
      </div>
    </section>
  );
};

export default Contact;
